import {
    useRef,
    useImperativeHandle,
    forwardRef,
    useState,
    useEffect,
    useCallback
} from "react";

import { cn } from "@/utils";

export interface ProgressBarHandle {
    setProgress: (value: number) => void;
}

interface Props {
    initialProgress?: number; // optional
    irisStatus?: 'open' | 'shrink';
    onAnimateEnd?: () => void;
}

export const ProgressBorder = forwardRef<ProgressBarHandle, Props>(
    ({ initialProgress = 0, irisStatus, onAnimateEnd }, ref) => {
        const [progress, setProgress] = useState(initialProgress);
        const queue = useRef<number[]>([]);
        const animating = useRef(false);

        const progressRef = useRef(initialProgress);

        const setNextProgress = useCallback(() => {
            if (animating.current) return;
            if (queue.current.length === 0) return;
            const nextProgress = queue.current.shift()!;
            animating.current = true;
            setProgress(nextProgress);
        }, []);

        useEffect(() => {
            const start = performance.now();
            const duration = 700; // 700 milliseconds
            const targetProgress = progress;
            const animate = (timestamp: number) => {
                const elapsed = timestamp - start;
                const progressFraction = Math.min(elapsed / duration, 1);

                if (progressFraction < 1) {
                    requestAnimationFrame(animate);
                }
                else {
                    progressRef.current = targetProgress; // Update progressRef to the final value
                    animating.current = false;
                    if (queue.current.length === 0) {
                        onAnimateEnd?.();

                    }
                    setNextProgress();
                }

            };
            requestAnimationFrame(animate);
        }, [progress, setNextProgress, onAnimateEnd]);

        useImperativeHandle(ref, () => ({
            setProgress: (value: number) => {
                queue.current.push(value);
                setNextProgress();
            }
        }));

        return (
            <div
                className="absolute w-full h-full rounded-full z-3 top-0 left-0"
                data-testid="progress-border-container"
            >
                <div
                    className={cn(
                        "absolute inset-[9.5px] rounded-full",
                        irisStatus === 'shrink' ? ' closing' : '',
                    )}
                    data-testid="progress-border">
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 100"
                        className="absolute inset-0"
                        style={{ overflow: 'visible' }}
                    >
                        <defs>
                            <linearGradient id="progress-gradient" x1="1" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="var(--color-primary-400)" />
                                <stop offset="95%" stopColor="white" />
                            </linearGradient>
                            <filter id="outer-glow" x="-20%" y="-20%" width="140%" height="140%">
                                <feGaussianBlur stdDeviation="2" result="blur" />
                                <feComponentTransfer>
                                    <feFuncA type="linear" slope="0.6" />
                                </feComponentTransfer>
                            </filter>
                        </defs>
                        {/* Progress Track (background) */}
                        <circle
                            cx="50"
                            cy="50"
                            r="55"
                            fill="none"
                            stroke="#000000ff"
                            strokeWidth="4"
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset 0.7s' }}
                            transform="rotate(-90 50 50)"
                        />
                        {/* Glow (blurred, wider stroke, behind) */}
                        <circle
                            cx="50"
                            cy="50"
                            r="55"
                            fill="none"
                            stroke="var(--color-primary-400)"
                            // stroke="url(#progress-gradient)"
                            strokeWidth="10"
                            strokeDasharray={Math.PI * 2 * 55}
                            strokeDashoffset={((100 - progress) / 100) * Math.PI * 2 * 55}
                            strokeLinecap="round"
                            filter="url(#outer-glow)"
                            style={{ opacity: 0.35, transition: 'stroke-dashoffset 0.7s' }}
                            transform="rotate(-90 50 50)"
                            data-testid="progress-glow-circle"
                        />
                        {/* Main progress (sharp, on top) */}
                        <circle
                            cx="50"
                            cy="50"
                            r="55"
                            fill="none"
                            stroke="var(--color-primary-400)"
                            strokeWidth="4"
                            strokeDasharray={Math.PI * 2 * 55}
                            strokeDashoffset={((100 - progress) / 100) * Math.PI * 2 * 55}
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset 0.7s' }}
                            transform="rotate(-90 50 50)"
                            data-testid="progress-circle"
                        />
                    </svg>
                </div>


                {/* <div className={`absolute inset-3 bg-background rounded-full`} /> */}

            </div>
        );
    }
);

ProgressBorder.displayName = 'ProgressBorder';
